// src/pages/finance-portal/payments/Payments.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, IconButton, Alert, Skeleton,
    MenuItem, Select, FormControl, InputLabel, Tooltip, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentForm from './PaymentForm';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    COMPLETED: { bg: '#dcfce7', color: '#166534' },
    PENDING: { bg: '#fef3c7', color: '#92400e' },
    FAILED: { bg: '#fecaca', color: '#991b1b' },
    RETURNED: { bg: '#ffedd5', color: '#9a3412' },
    REFUNDED: { bg: '#dbeafe', color: '#1e40af' },
    VOID: { bg: '#f1f5f9', color: '#475569' },
};

const PAYMENT_METHOD_LABELS = {
    CASH: 'Cash',
    CHECK: 'Check',
    BANK_TRANSFER: 'Bank Transfer',
    CREDIT_CARD: 'Credit Card',
    DEBIT_CARD: 'Debit Card',
    ACH: 'ACH',
    ONLINE: 'Online',
    OTHER: 'Other',
};

const PAYMENT_METHOD_OPTIONS = [
    { value: '', label: 'All Methods' },
    { value: 'CASH', label: 'Cash' },
    { value: 'CHECK', label: 'Check' },
    { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
    { value: 'CREDIT_CARD', label: 'Credit Card' },
    { value: 'DEBIT_CARD', label: 'Debit Card' },
    { value: 'ACH', label: 'ACH' },
    { value: 'ONLINE', label: 'Online' },
    { value: 'OTHER', label: 'Other' },
];

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

export default function Payments() {
    const [payments, setPayments] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [methodFilter, setMethodFilter] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // Form dialog
    const [formOpen, setFormOpen] = useState(false);

    // View dialog
    const [viewPayment, setViewPayment] = useState(null);

    // Void dialog
    const [voidDialog, setVoidDialog] = useState({ open: false, payment: null });
    const [voiding, setVoiding] = useState(false);

    // Delete dialog
    const [deleteDialog, setDeleteDialog] = useState({ open: false, payment: null });
    const [deleting, setDeleting] = useState(false);

    const fetchPayments = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (search) params.search = search;
            if (methodFilter) params.payment_method = methodFilter;
            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;

            const res = await axiosInstance.get('/api/finance/payments', { params });
            if (res.data.success) {
                setPayments(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load payments');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, search, methodFilter, fromDate, toDate]);

    useEffect(() => { fetchPayments(); }, [fetchPayments]);

    const handleView = async (paymentId) => {
        try {
            const res = await axiosInstance.get(`/api/finance/payments/${paymentId}`);
            if (res.data.success) setViewPayment(res.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load payment details');
        }
    };

    const handleVoid = async () => {
        setVoiding(true);
        try {
            await axiosInstance.post(`/api/finance/payments/${voidDialog.payment.id}/void`);
            setVoidDialog({ open: false, payment: null });
            fetchPayments();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to void payment');
        } finally {
            setVoiding(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axiosInstance.delete(`/api/finance/payments/${deleteDialog.payment.id}`);
            setDeleteDialog({ open: false, payment: null });
            fetchPayments();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete payment');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <PaymentIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Payments</Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Track and manage received payments</Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Toolbar */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Search payments..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                            sx: { fontFamily, borderRadius: '10px' },
                        }}
                        sx={{ minWidth: 200 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel sx={{ fontFamily }}>Payment Method</InputLabel>
                        <Select
                            value={methodFilter}
                            onChange={e => { setMethodFilter(e.target.value); setPage(0); }}
                            label="Payment Method"
                            sx={{ fontFamily, borderRadius: '10px' }}
                        >
                            {PAYMENT_METHOD_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily }}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        size="small"
                        type="date"
                        label="From Date"
                        value={fromDate}
                        onChange={e => { setFromDate(e.target.value); setPage(0); }}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        sx={{ minWidth: 150 }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="To Date"
                        value={toDate}
                        onChange={e => { setToDate(e.target.value); setPage(0); }}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        sx={{ minWidth: 150 }}
                    />
                    <Box sx={{ flex: 1 }} />
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setFormOpen(true)}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        Record Payment
                    </Button>
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Payment #', 'Customer', 'Invoice #', 'Date', 'Amount', 'Method', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={8}><Skeleton height={36} /></TableCell>
                                        </TableRow>
                                    ))
                                ) : payments.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No payments found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : payments.map(payment => {
                                    const statusStyle = STATUS_COLORS[payment.status] || {};
                                    const methodLabel = PAYMENT_METHOD_LABELS[payment.payment_method] || payment.payment_method || '—';
                                    const customerName = payment.customer_name || payment.customer?.company_name || payment.customer?.name || '—';
                                    const invoiceNumber = payment.invoice_number || payment.invoice?.invoice_number || '—';

                                    return (
                                        <TableRow key={payment.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                {payment.payment_number || `PMT-${payment.id}`}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {customerName}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {invoiceNumber}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {payment.payment_date}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                {formatCurrency(payment.amount)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {methodLabel}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={payment.status}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                        bgcolor: statusStyle.bg, color: statusStyle.color,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="View">
                                                        <IconButton size="small" onClick={() => handleView(payment.id)} sx={{ color: '#94a3b8' }}>
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {payment.status !== 'VOID' && payment.status !== 'REFUNDED' && (
                                                        <Tooltip title="Void">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setVoidDialog({ open: true, payment })}
                                                                sx={{ color: '#94a3b8', '&:hover': { color: '#f59e0b' } }}
                                                            >
                                                                <BlockIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setDeleteDialog({ open: true, payment })}
                                                            sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Payment Form Dialog */}
            <PaymentForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={fetchPayments} />

            {/* View Payment Dialog */}
            <Dialog open={!!viewPayment} onClose={() => setViewPayment(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {viewPayment?.payment_number || `PMT-${viewPayment?.id}`}
                    {viewPayment?.status && (
                        <Chip
                            label={viewPayment.status}
                            size="small"
                            sx={{
                                ml: 1, fontFamily, fontSize: '0.7rem', fontWeight: 600,
                                bgcolor: (STATUS_COLORS[viewPayment.status] || {}).bg,
                                color: (STATUS_COLORS[viewPayment.status] || {}).color,
                            }}
                        />
                    )}
                </DialogTitle>
                <DialogContent>
                    {viewPayment && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: 4 }}>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Customer</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b', fontWeight: 500 }}>
                                        {viewPayment.customer_name || viewPayment.customer?.company_name || viewPayment.customer?.name || '—'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Invoice</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b', fontWeight: 500 }}>
                                        {viewPayment.invoice_number || viewPayment.invoice?.invoice_number || '—'}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 4 }}>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Amount</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '1.2rem', color: ACCENT, fontWeight: 700 }}>
                                        {formatCurrency(viewPayment.amount)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Date</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b', fontWeight: 500 }}>
                                        {viewPayment.payment_date}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Method</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b', fontWeight: 500 }}>
                                        {PAYMENT_METHOD_LABELS[viewPayment.payment_method] || viewPayment.payment_method || '—'}
                                    </Typography>
                                </Box>
                            </Box>
                            {viewPayment.reference_number && (
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Reference Number</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b', fontWeight: 500 }}>{viewPayment.reference_number}</Typography>
                                </Box>
                            )}
                            {viewPayment.notes && (
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Notes</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', whiteSpace: 'pre-wrap' }}>{viewPayment.notes}</Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setViewPayment(null)} sx={{ fontFamily, textTransform: 'none' }}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Void Confirmation */}
            <Dialog open={voidDialog.open} onClose={() => setVoidDialog({ open: false, payment: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Void {voidDialog.payment?.payment_number || `PMT-${voidDialog.payment?.id}`}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will void the payment of {formatCurrency(voidDialog.payment?.amount)}. If this payment was applied to an invoice, the invoice balance will be restored.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setVoidDialog({ open: false, payment: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleVoid}
                        disabled={voiding}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
                    >
                        {voiding ? 'Voiding...' : 'Void Payment'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, payment: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete {deleteDialog.payment?.payment_number || `PMT-${deleteDialog.payment?.id}`}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will permanently delete this payment record. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialog({ open: false, payment: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
